import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StatePanel, Icon } from '@sakhtar/design-system/react';
import blackLogoUrl from '@sakhtar/design-system/assets/logo/sakhtar-black-logo.svg';
import { apiGet, apiPatch, apiPost, ApiError } from '../../lib/apiClient.js';
import type { FieldProblem } from '../../lib/errorEnvelope.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { WelcomeStep } from './WelcomeStep.js';
import { BaseOrganizationStep } from './BaseOrganizationStep.js';
import { ModuleAnswersStep } from './ModuleAnswersStep.js';
import { TeamStep } from './TeamStep.js';
import { ReviewStep } from './ReviewStep.js';
import { ProvisioningStep } from './ProvisioningStep.js';
import '../onboarding-shell.css';
import { moduleQuestionConfigFor, PREVIOUS_DATA_QUESTION_CONFIG } from './moduleAnswerConfig.js';
import {
  MODULE_STEP_KEYS,
  PREVIOUS_DATA_STEP_KEY,
  SETUP_STEP_KEYS,
  SETUP_STEP_LABEL,
  TEAM_STEP_KEY,
  baseOrganizationPayloadOf,
  isSetupStepKey,
  moduleAnswersOf,
  setupStepDone,
  setupStepView,
  type ModuleStepKey,
  type SetupStepKey,
  type SetupView,
} from '../../lib/types/setup.js';

type ActiveView = SetupStepKey | 'review' | 'provisioning';

/**
 * Customer-facing Setup Wizard — `t/:slug/api/v1/setup`, phase 1 of a
 * user-directed reconciliation against `Sakhtar Onboarding Team Desktop.html`
 * / `Sakhtar Onboarding Team and Review Desktop.html`. This pass moves the
 * SHELL only: the route no longer nests under `AuthenticatedShell` (real app
 * sidebar/topbar with every module's nav) — it now owns a dedicated
 * full-bleed shell (topbar + a setup-only left rail), matching both boards'
 * own shell (topbar height 64 / rail ~284px / same rail circle states) and
 * this app's own established precedent for a standalone onboarding flow
 * (`start/TrialOnboardingPage.tsx`). Every step's CONTENT and every API call
 * is unchanged in this pass — no local fake setup state, `view` is nothing
 * but the server's last `SetupView` response. The richer Phase C redesign
 * (real per-module-capability access model, deferred invitations, a
 * provisioning/completion pair, a Home checklist widget) is explicitly
 * deferred to later phases — see the PR description for why each needs its
 * own backend decision first, not a UI-only pass.
 *
 * Losing `AuthenticatedShell`'s wrapper loses nothing on session expiry: a
 * 401 from *any* API call already redirects to `/t/:slug/login` at the
 * `apiClient.ts` layer (see `session-expiration-redirect.test.ts`), not
 * inside that shell — this page's own `/api/v1/setup` call is covered the
 * same way every other tenant route already is. A 403/404 on this page's
 * own load keeps this page's own, more specific `StatePanel`, same as
 * before this pass.
 *
 * Later phases (Team C1-C3's real per-module-capability access model with
 * deferred invitations, and this provisioning pair) have since landed on
 * top of this shell — see `TeamStep.tsx`'s and `ProvisioningStep.tsx`'s own
 * notes. Only the C8 Home checklist (`HomePage.tsx`'s own note) and C5's
 * full per-field review summary remain intentionally simplified.
 *
 * UI_REFERENCE_SOURCE:
 * section: shell — Sakhtar Onboarding Team Desktop.html's own topbar +
 *   "راه‌اندازی" left rail (فاز N از ۳, per-step circle states, progress
 *   bar), re-read directly (DesignSync) 2026-09-25. Per-step content
 *   citations are unchanged from before this pass — see each step's own
 *   file for its own note (`WelcomeStep.tsx`/`BaseOrganizationStep.tsx`: no
 *   dedicated frame, `ModuleAnswersStep.tsx`: Sakhtar Onboarding Setup
 *   Desktop.html, `TeamStep.tsx`/`ReviewStep.tsx`: the two Team boards,
 *   still PARTIAL_REAL pending the later phases above).
 * archetype: dedicated onboarding shell — topbar + left rail — over a
 *   resume-from-server multi-step wizard.
 * status: KIT_CONFORMANT for the shell; per-step status unchanged from
 *   before this pass.
 * state reference: loading · error (setup load failed) · ready (per-step
 *   busy/error passed down to each step component)
 */
export function SetupWizardPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const base = slug ? tenantBasePath(slug) : '';

  const [phase, setPhase] = useState<'loading' | 'error' | 'ready'>('loading');
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [view, setView] = useState<SetupView | null>(null);
  const [active, setActive] = useState<ActiveView>('welcome');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionErrorDetails, setActionErrorDetails] = useState<readonly FieldProblem[] | undefined>(
    undefined,
  );
  const [provPhase, setProvPhase] = useState<'running' | 'done' | 'failed'>('running');
  const [provError, setProvError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        let next = await apiGet<SetupView>(`${base}/api/v1/setup`);
        if (next.isTenantAdmin === false) {
          if (!cancelled) navigate(`${base}/admin`, { replace: true });
          return;
        }
        if (next.status === 'NOT_STARTED') {
          next = await apiPatch<SetupView>(`${base}/api/v1/setup/current-step`, {
            stepKey: next.currentStepKey,
          });
        }
        if (!cancelled) {
          setView(next);
          setActive(allStepsDone(next) ? 'review' : next.currentStepKey);
          setPhase('ready');
        }
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err : networkError());
        setPhase('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, base]);

  if (!slug) return null;

  if (phase === 'loading') {
    // Blank until the server says this caller is a tenant-admin. A loading
    // chrome here would flash the wizard at non-admins before the redirect.
    return null;
  }
  if (phase === 'error' || view === null) {
    return (
      <div
        className="sk-app"
        dir="rtl"
        style={{
          width: '100%',
          minHeight: '100dvh',
          background: 'var(--sk-bg-canvas)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <StatePanel
          variant={loadError?.status === 403 ? 'permission_denied' : 'network_error'}
          title={loadError?.status === 403 ? 'دسترسی ندارید' : 'راه‌اندازی قابل نمایش نیست'}
          description={loadError?.status === 403 ? undefined : 'دوباره تلاش کنید.'}
        />
      </div>
    );
  }

  // Skipped while `active === 'provisioning'`: `provision()` below sets
  // `view` to the just-completed run (status COMPLETED) as soon as the
  // real POST resolves, specifically so the C7 "done" screen can show real
  // data — this guard would otherwise redirect away before the user ever
  // sees it. It still fires on every other path (a direct load or reload
  // of /setup on an already-completed run), which is its real job.
  if (view.status === 'COMPLETED' && active !== 'provisioning') {
    navigate(`${base}/admin`, { replace: true });
    return null;
  }

  /** Returns whether `action` succeeded — a caught error here never rejects,
   * so a caller that must act only on success (navigate away) needs the
   * boolean rather than chaining off the promise. */
  async function runAndReport(
    action: () => Promise<SetupView>,
    nextView?: ActiveView,
  ): Promise<boolean> {
    setBusy(true);
    setActionError(null);
    setActionErrorDetails(undefined);
    try {
      const next = await action();
      setView(next);
      setActive(nextView ?? (allStepsDone(next) ? 'review' : next.currentStepKey));
      return true;
    } catch (err) {
      setActionError(friendlyActionError(err));
      setActionErrorDetails(err instanceof ApiError ? err.envelope.error.details : undefined);
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** Same as `runAndReport`, for the (common) case where the caller doesn't need to branch on success. */
  async function run(action: () => Promise<SetupView>, nextView?: ActiveView): Promise<void> {
    await runAndReport(action, nextView);
  }

  async function goToStep(key: SetupStepKey): Promise<void> {
    await run(
      () => apiPatch<SetupView>(`${base}/api/v1/setup/current-step`, { stepKey: key }),
      key,
    );
  }

  /** The real, single atomic `POST .../setup/complete` — see
   * `ProvisioningStep.tsx`'s own note on why this is one honest request,
   * not a fabricated multi-phase animation. */
  async function provision(): Promise<void> {
    setActive('provisioning');
    setProvPhase('running');
    setProvError(null);
    try {
      const next = await apiPost<SetupView>(`${base}/api/v1/setup/complete`);
      setView(next);
      setProvPhase('done');
    } catch (err) {
      setProvError(friendlyActionError(err));
      setProvPhase('failed');
    }
  }

  function onRailClick(id: string): void {
    if (id === 'review') {
      setActive('review');
      return;
    }
    if (!isSetupStepKey(id)) return;
    const step = setupStepView(view!, id);
    if (step.status === 'BLOCKED') return;
    void goToStep(id);
  }

  const doneCount = SETUP_STEP_KEYS.filter((key) =>
    setupStepDone(setupStepView(view, key).status),
  ).length;
  const percent = Math.round((doneCount / SETUP_STEP_KEYS.length) * 100);
  const showReviewTab = allStepsDone(view);

  const rail: readonly {
    id: ActiveView;
    label: string;
    isDone: boolean;
    isCurrent: boolean;
    isPending: boolean;
  }[] = [
    ...SETUP_STEP_KEYS.map((key) => {
      const status = setupStepView(view, key).status;
      return {
        id: key,
        label: SETUP_STEP_LABEL[key],
        isDone: setupStepDone(status),
        isCurrent: active === key,
        isPending: !setupStepDone(status) && active !== key,
      };
    }),
    ...(showReviewTab
      ? [
          {
            id: 'review' as ActiveView,
            label: 'مرور و تکمیل',
            isDone: false,
            isCurrent: active === 'review',
            isPending: active !== 'review',
          },
        ]
      : []),
  ];

  return (
    <div
      className="sk-app"
      dir="rtl"
      style={{
        width: '100%',
        minHeight: '100dvh',
        background: 'var(--sk-bg-canvas)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* TOPBAR */}
      <div
        className="sk-onb-topbar"
        style={{
          flex: '0 0 auto',
          height: 64,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 28px',
          background: 'var(--sk-bg-elev)',
          borderBottom: '1px solid var(--sk-border)',
        }}
      >
        <img src={blackLogoUrl} alt="ساختار" width={22} height={22} style={{ display: 'block' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sk-fg)' }}>ساختار سیستم</span>
          <span
            className="sk-onb-topbar-subtitle"
            style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}
          >
            راه‌اندازی اولیه سیستم
          </span>
        </div>
      </div>

      {/* MOBILE-ONLY compact progress — the rail (with its own progress
          bar) is hidden below 899px; this is the same doneCount/percent
          data, just a one-line readout instead of the full step list. */}
      <div
        className="sk-onb-mobile-progress"
        style={{
          display: 'none',
          flexDirection: 'column',
          gap: 6,
          padding: '10px 16px',
          background: 'var(--sk-bg-elev)',
          borderBottom: '1px solid var(--sk-border)',
        }}
      >
        <div className="sk-prog">
          <div className="sk-prog__fill" style={{ width: `${percent}%` }} />
        </div>
        <span style={{ fontSize: 11.5, color: 'var(--sk-fg-muted)' }}>
          {doneCount.toLocaleString('fa-IR')} از {SETUP_STEP_KEYS.length.toLocaleString('fa-IR')}{' '}
          بخش ({percent.toLocaleString('fa-IR')}٪) تکمیل شده
        </span>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* LEFT RAIL */}
        <div
          className="sk-onb-rail"
          style={{
            flex: '0 0 284px',
            background: 'var(--sk-bg-elev)',
            borderInlineStart: '1px solid var(--sk-border)',
            padding: '28px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '.05em',
                color: 'var(--sk-fg-subtle)',
              }}
            >
              راه‌اندازی اولیه
            </span>
            <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--sk-fg)' }}>
              مراحل راه‌اندازی
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {rail.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => onRailClick(r.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 11,
                  height: 42,
                  padding: '0 10px',
                  border: 0,
                  borderRadius: 'var(--sk-r-sm)',
                  background: r.isCurrent ? 'var(--sk-bg-canvas)' : 'transparent',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  textAlign: 'right',
                  width: '100%',
                }}
              >
                {r.isDone ? (
                  <span
                    style={{
                      width: 21,
                      height: 21,
                      borderRadius: '50%',
                      background: 'var(--sk-success)',
                      color: '#fff',
                      display: 'grid',
                      placeContent: 'center',
                      flex: '0 0 auto',
                    }}
                  >
                    <Icon name="check" size="sm" />
                  </span>
                ) : r.isCurrent ? (
                  <span
                    style={{
                      width: 21,
                      height: 21,
                      borderRadius: '50%',
                      border: '2px solid var(--sk-brand-500)',
                      display: 'grid',
                      placeContent: 'center',
                      flex: '0 0 auto',
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'var(--sk-brand-500)',
                      }}
                    />
                  </span>
                ) : (
                  <span
                    style={{
                      width: 21,
                      height: 21,
                      borderRadius: '50%',
                      border: '1.5px solid var(--sk-border-strong)',
                      flex: '0 0 auto',
                    }}
                  />
                )}
                <span
                  style={{
                    fontSize: 13.5,
                    fontWeight: r.isCurrent ? 600 : 400,
                    color: r.isCurrent ? 'var(--sk-fg)' : 'var(--sk-fg-subtle)',
                  }}
                >
                  {r.label}
                </span>
              </button>
            ))}
          </div>
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 9 }}>
            <div className="sk-prog">
              <div className="sk-prog__fill" style={{ width: `${percent}%` }} />
            </div>
            <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
              {doneCount.toLocaleString('fa-IR')} از{' '}
              {SETUP_STEP_KEYS.length.toLocaleString('fa-IR')} بخش (
              {percent.toLocaleString('fa-IR')}٪) تکمیل شده
            </span>
          </div>
        </div>

        {/* MAIN CONTENT AREA */}
        <div
          className="sk-onb-content"
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            padding: '44px 32px 60px',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 720,
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            {renderActive({
              active,
              view,
              base,
              slug,
              busy,
              actionError,
              actionErrorDetails,
              run,
              goToStep,
              setActive,
              navigate,
              doneCount,
              provPhase,
              provError,
              provision,
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function renderActive(props: {
  active: ActiveView;
  view: SetupView;
  base: string;
  slug: string;
  busy: boolean;
  actionError: string | null;
  actionErrorDetails: readonly FieldProblem[] | undefined;
  run: (action: () => Promise<SetupView>, nextView?: ActiveView) => Promise<void>;
  goToStep: (key: SetupStepKey) => Promise<void>;
  setActive: (v: ActiveView) => void;
  navigate: ReturnType<typeof useNavigate>;
  doneCount: number;
  provPhase: 'running' | 'done' | 'failed';
  provError: string | null;
  provision: () => Promise<void>;
}) {
  const {
    active,
    view,
    base,
    slug,
    busy,
    actionError,
    actionErrorDetails,
    run,
    goToStep,
    setActive,
    navigate,
    doneCount,
    provPhase,
    provError,
    provision,
  } = props;

  if (active === 'review') {
    return (
      <ReviewStep
        view={view}
        onEditStep={(key) => void goToStep(key)}
        onFinish={provision}
        busy={busy}
        error={actionError}
      />
    );
  }

  if (active === 'provisioning') {
    return (
      <ProvisioningStep
        phase={provPhase}
        slug={slug}
        stepsDone={doneCount}
        stepsTotal={SETUP_STEP_KEYS.length}
        error={provError}
        onRetry={() => void provision()}
        onBackToReview={() => setActive('review')}
        onEnter={() => navigate(`${base}/admin`, { replace: true })}
      />
    );
  }

  if (active === 'welcome') {
    return (
      <WelcomeStep
        busy={busy}
        error={actionError}
        onContinue={() =>
          void run(() =>
            apiPost<SetupView>(`${base}/api/v1/setup/complete-step`, { stepKey: 'welcome' }),
          )
        }
      />
    );
  }

  if (active === 'base-organization') {
    const orgStep = setupStepView(view, 'base-organization');
    return (
      <BaseOrganizationStep
        invariants={view.invariants}
        initialPayload={baseOrganizationPayloadOf(orgStep)}
        busy={busy}
        error={actionError}
        onBack={() => void goToStep('welcome')}
        onSave={(patch) =>
          run(() => apiPatch<SetupView>(`${base}/api/v1/setup/base-organization`, patch))
        }
        onComplete={(patch) =>
          run(async () => {
            await apiPatch<SetupView>(`${base}/api/v1/setup/base-organization`, patch);
            return apiPost<SetupView>(`${base}/api/v1/setup/complete-step`, {
              stepKey: 'base-organization',
            });
          })
        }
      />
    );
  }

  if ((MODULE_STEP_KEYS as readonly string[]).includes(active)) {
    const key = active as ModuleStepKey;
    const step = setupStepView(view, key);
    return (
      <ModuleAnswersStep
        // One instance per step: without `key` React reuses the component
        // across module steps and its draft carries the previous step's
        // answers (e.g. `salesModel`) into the next step's PATCH → 400.
        key={key}
        config={moduleQuestionConfigFor(key, view?.chartOfAccountsChoices)}
        initialAnswers={moduleAnswersOf(step)}
        busy={busy}
        error={actionError}
        errorDetails={actionErrorDetails}
        onBack={() => void goToStep(previousKeyOf(key))}
        onSave={(patch) =>
          run(() => apiPatch<SetupView>(`${base}/api/v1/setup/steps/${key}`, patch))
        }
        onComplete={(patch) =>
          run(async () => {
            await apiPatch<SetupView>(`${base}/api/v1/setup/steps/${key}`, patch);
            return apiPost<SetupView>(`${base}/api/v1/setup/complete-step`, { stepKey: key });
          })
        }
      />
    );
  }

  if (active === TEAM_STEP_KEY) {
    return (
      <TeamStep
        base={base}
        busy={busy}
        error={actionError}
        onBack={() => void goToStep(previousKeyOf(TEAM_STEP_KEY))}
        onComplete={() =>
          run(() =>
            apiPost<SetupView>(`${base}/api/v1/setup/complete-step`, { stepKey: TEAM_STEP_KEY }),
          )
        }
      />
    );
  }

  if (active === PREVIOUS_DATA_STEP_KEY) {
    const step = setupStepView(view, PREVIOUS_DATA_STEP_KEY);
    return (
      <ModuleAnswersStep
        config={PREVIOUS_DATA_QUESTION_CONFIG}
        initialAnswers={moduleAnswersOf(step)}
        busy={busy}
        error={actionError}
        errorDetails={actionErrorDetails}
        onBack={() => void goToStep(previousKeyOf(PREVIOUS_DATA_STEP_KEY))}
        onSave={(patch) =>
          run(() =>
            apiPatch<SetupView>(`${base}/api/v1/setup/steps/${PREVIOUS_DATA_STEP_KEY}`, patch),
          )
        }
        onComplete={(patch) =>
          run(async () => {
            await apiPatch<SetupView>(
              `${base}/api/v1/setup/steps/${PREVIOUS_DATA_STEP_KEY}`,
              patch,
            );
            return apiPost<SetupView>(`${base}/api/v1/setup/complete-step`, {
              stepKey: PREVIOUS_DATA_STEP_KEY,
            });
          })
        }
      />
    );
  }

  setActive('welcome');
  return null;
}

function previousKeyOf(key: SetupStepKey): SetupStepKey {
  const index = SETUP_STEP_KEYS.indexOf(key);
  return SETUP_STEP_KEYS[index - 1] ?? key;
}

function allStepsDone(view: SetupView): boolean {
  return SETUP_STEP_KEYS.every((key) => setupStepDone(setupStepView(view, key).status));
}

function networkError(): ApiError {
  return new ApiError(0, { error: { code: 'INTERNAL', message: 'Network error', requestId: '' } });
}

/** Same "map by code, never surface the raw envelope message" pattern every other form in this app uses. */
function friendlyActionError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'VALIDATION_FAILED') {
      return 'اطلاعات وارد‌شده نامعتبر است — مقادیر ثبت‌شده را بررسی کنید.';
    }
    if (err.status === 409) {
      return 'این مرحله با وضعیت فعلی راه‌اندازی هم‌خوانی ندارد. صفحه را دوباره بارگذاری کنید.';
    }
    if (err.status === 403 || err.status === 404) {
      return 'دسترسی به راه‌اندازی ندارید.';
    }
  }
  return 'عملیات ناموفق بود. دوباره تلاش کنید.';
}
